/**
 * Pure helpers for the S60b run panel (plan 2 §10.2).
 */

/** @type {Set<string>} */
export const FINAL_STATUSES = new Set([
  'completed',
  'completed_partial',
  'failed',
  'cancelled',
]);

export const STATUS_ICON = {
  pending: '○',
  running: '●',
  done: '✓',
  failed: '✕',
  skipped: '–',
};

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isFinalStatus(status) {
  return FINAL_STATUSES.has(String(status || ''));
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isActiveStatus(status) {
  return !!status && !isFinalStatus(status);
}

/**
 * @param {object[]} steps
 * @param {object} step
 * @returns {object[]}
 */
export function applyStepEvent(steps, step) {
  if (!step || typeof step !== 'object' || step.id == null) {
    return Array.isArray(steps) ? steps.slice() : [];
  }
  const list = Array.isArray(steps) ? steps.slice() : [];
  const id = String(step.id);
  const index = list.findIndex((row) => row && String(row.id) === id);
  if (index >= 0) list[index] = { ...list[index], ...step };
  else list.push({ ...step });
  return list;
}

/**
 * @param {object|null} run
 * @param {{ status?: string, status_detail?: string, stage?: string }} patch
 * @returns {object|null}
 */
export function applyStatusEvent(run, patch) {
  if (!run || typeof run !== 'object') return run;
  const next = { ...run };
  if (patch && patch.status != null) next.status = patch.status;
  if (patch && patch.status_detail != null) next.status_detail = patch.status_detail;
  if (patch && patch.stage != null) next.stage = patch.stage;
  return next;
}

/**
 * @param {object|null} run
 * @param {{ userId?: string, isAdmin?: boolean }} who
 * @returns {boolean}
 */
export function canControlRun(run, who) {
  if (!run || typeof run !== 'object') return false;
  if (isFinalStatus(run.status)) return false;
  if (who && who.isAdmin) return true;
  const uid = who && who.userId != null ? String(who.userId) : '';
  if (!uid) return false;
  return String(run.requested_by || '') === uid;
}

/**
 * @param {boolean} socketOpen
 * @param {unknown} status
 * @returns {boolean}
 */
export const QUIET_POLL_MS = 10000;

/**
 * Poll while a run is active and either the socket is down or no live update
 * has arrived for QUIET_POLL_MS (an open socket can still be silent).
 * @param {boolean} socketOpen
 * @param {unknown} status
 * @param {number} [quietMs] time since the last live update
 */
export function shouldPoll(socketOpen, status, quietMs = 0) {
  if (!isActiveStatus(status)) return false;
  return !socketOpen || quietMs >= QUIET_POLL_MS;
}

/**
 * @param {unknown} models
 * @returns {string}
 */
export function formatModels(models) {
  let map = models;
  if (typeof map === 'string') {
    try { map = JSON.parse(map); } catch { map = null; }
  }
  if (!map || typeof map !== 'object' || Array.isArray(map)) return '';
  const parts = [];
  for (const [role, id] of Object.entries(map)) {
    if (id == null || id === '') continue;
    parts.push(`${role}: ${id}`);
  }
  return parts.join(' · ');
}

/**
 * @param {string|null|undefined} start
 * @param {string|null|undefined} end
 * @param {number} [nowMs]
 * @returns {string}
 */
export function formatElapsed(start, end, nowMs) {
  if (!start) return '';
  const from = Date.parse(String(start));
  if (!Number.isFinite(from)) return '';
  const to = end ? Date.parse(String(end)) : (nowMs != null ? nowMs : Date.now());
  if (!Number.isFinite(to) || to < from) return '';
  const sec = Math.floor((to - from) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) return `${min}m ${rem}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

/**
 * @param {object} step
 * @returns {string}
 */
export function stepDuration(step) {
  if (!step || !step.started_at) return '';
  return formatElapsed(step.started_at, step.ended_at);
}

/**
 * @param {object[]} steps
 * @returns {{ step: object, tools: object[] }[]}
 */
export function groupSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const children = new Map();
  for (const step of list) {
    if (!step || !step.parent_id) continue;
    const key = String(step.parent_id);
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(step);
  }
  return list
    .filter((step) => step && !step.parent_id)
    .map((step) => ({
      step,
      tools: children.get(String(step.id)) || [],
    }));
}

/**
 * Parse ```run / ```retry fences from message content (S59b / S60a).
 * @param {string} content
 * @returns {{ type: string, id: string, resolved: { run_id: string } }[]}
 */
export function extractRunRetryBlocks(content) {
  const text = String(content || '');
  const re = /```(run|retry)(?![:\w-])[^\n]*\r?\n([\s\S]*?)```/g;
  const out = [];
  let match = re.exec(text);
  let n = 0;
  while (match) {
    n += 1;
    let runId = '';
    try {
      const parsed = JSON.parse(String(match[2] || '').trim());
      if (parsed && typeof parsed === 'object' && parsed.run_id != null) {
        runId = String(parsed.run_id);
      }
    } catch {
      runId = '';
    }
    out.push({
      id: `${match[1]}-${n}`,
      type: match[1],
      resolved: { run_id: runId },
    });
    match = re.exec(text);
  }
  return out;
}
